import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { listNotices, markNoticeRead } from "../../entities/notice/api";
import { getNoticeItems, normalizeNotice } from "../../entities/notice/normalize.js";
import { getApiErrorMessage } from "../../shared/api/errors";
import { IS_API_MODE } from "../../shared/config/runtime";
import logoAsset from "../../assets/tupi-logo.png";

function noticeAvatar() {
  return logoAsset;
}

function noticeTime(value) {
  if (!value) return "刚刚";
  return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export default function NotificationsPopover({ onClose }) {
  const queryClient = useQueryClient();
  const noticesQuery = useQuery({
    queryKey: ["notices"],
    queryFn: () => listNotices(),
    enabled: IS_API_MODE,
    staleTime: 30_000,
  });
  const readMutation = useMutation({
    mutationFn: markNoticeRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notices"] }),
  });
  const notices = useMemo(() => {
    return getNoticeItems(noticesQuery.data).map(normalizeNotice).slice(0, 30);
  }, [noticesQuery.data]);

  return (
    <section className="notifications-popover" role="dialog" aria-label="通知">
      <header>
        <strong>通知</strong>
        <div className="notifications-popover-header-actions">
          {notices.length > 0 ? <span>{notices.filter((notice) => !notice.read_at).length} 条未读</span> : null}
          <button type="button" className="notifications-popover-close" aria-label="关闭通知" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
      </header>
      {noticesQuery.isPending ? <p className="notifications-popover-state">正在加载通知……</p> : null}
      {noticesQuery.isError ? <p className="notifications-popover-state error">{getApiErrorMessage(noticesQuery.error, "通知加载失败")}</p> : null}
      {!noticesQuery.isPending && !noticesQuery.isError && notices.length === 0 ? (
        <p className="notifications-popover-state">暂无通知</p>
      ) : null}
      <div className="notifications-popover-list">
        {notices.map((notice) => {
          const unread = !notice.readAt;
          return (
            <button
              className={`notifications-popover-item${unread ? " unread" : ""}`}
              key={notice.id}
              type="button"
              onClick={() => unread && readMutation.mutate(notice.id)}
            >
              <img className="avatar avatar-sm notice-logo-avatar" src={noticeAvatar(notice)} alt="" />
              <span>
                <strong>{notice.title}</strong>
                <small>{notice.body}</small>
                <time>{noticeTime(notice.createdAt)}</time>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
